import React, { useState } from 'react';

import { LoadingButton } from '@mui/lab';
import { Dialog, TextField, DialogTitle, DialogActions, DialogContent } from '@mui/material';

import useLocales from '../../../../hooks/useLocales';
import { useSelector } from '../../../../redux/store';
import useNotifications from '../../../../hooks/useNotifications';
import { createFolder } from '../../../../services/dlxDriveService';
import { setDriveListIdState } from '../../../../redux/slices/dlxDrive';
import { ModuleTypes } from '../../../../@types/module/enums/moduleTypes';
import ModuleCheck from '../../../../components/access-checks/ModuleCheck';
import { Permissions } from '../../../../@types/auth/constants/permissions';
import PermissionCheck from '../../../../components/access-checks/PermissionCheck';
import { bindWorkflow } from '../../../../services/workflow/workflowBindingService';
import WorkflowDefinitionPicker from '../../../shared/workflow/WorkflowDefinitionPicker';

// ----------------------------------------------------------------

type DLXDriveCreateFolderDialogProps = {
  isCreateFolderDialogOpen: boolean;
  setIsCreateFolderDialogOpen: (value: boolean) => void;
};

const DLXDriveCreateFolderDialog: React.FC<DLXDriveCreateFolderDialogProps> = ({
  isCreateFolderDialogOpen,
  setIsCreateFolderDialogOpen,
}) => {
  const { t } = useLocales();
  const { isLoading, selectedFolder } = useSelector((state) => state.dlxDrive);
  const [folderName, setFolderName] = useState<string>('');
  const [folderPrefix, setFolderPrefix] = useState<string>('');
  const [definitionNumber, setDefinitionNumber] = useState<number | ''>('');
  const { enqueueSuccess, enqueueError } = useNotifications();
  const maxLengthPrefix = 50;
  const maxLengthName = 100;

  const handleSave = async () => {
    try {
      const created = await createFolder({
        parentFolderId: selectedFolder.id,
        name: folderName,
        prefix: folderPrefix,
      });

      enqueueSuccess('dlxDrive.notifications.folderCreated');

      if (definitionNumber !== '' && created?.id) {
        try {
          await bindWorkflow('folder', created.id, definitionNumber);
        } catch {
          enqueueError('workflows.binding.saveFailed');
        }
      }

      setFolderName('');
      setFolderPrefix('');
      setDefinitionNumber('');
    } catch (error) {
      enqueueError(error.message);
    } finally {
      setDriveListIdState();
      handleClose();
    }
  };

  const handleClose = () => {
    setIsCreateFolderDialogOpen(false);
  };

  return (
    <Dialog open={isCreateFolderDialogOpen} fullWidth maxWidth="xs" onClose={handleClose}>
      <DialogTitle>{t('dlxDrive.dialogs.createNewFolder')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          id="new-folder-name"
          label={t('dlxDrive.dialogs.folderName')}
          variant="outlined"
          size="small"
          fullWidth
          value={folderName}
          onChange={(e) => setFolderName(e.target.value.replaceAll('/', ''))}
          helperText={
            folderName.length === maxLengthName
              ? `${t('dlxDrive.dialogs.maxFolderNameLengthReached', { maxLengthName })}`
              : ''
          }
          slotProps={{
            htmlInput: { maxLength: maxLengthName },
          }}
        />

        <TextField
          id="new-folder-prefix"
          label={t('dlxDrive.dialogs.folderPrefix')}
          variant="outlined"
          size="small"
          fullWidth
          value={folderPrefix}
          onChange={(e) => setFolderPrefix(e.target.value)}
          helperText={
            folderPrefix.length === maxLengthPrefix
              ? `${t('dlxDrive.dialogs.maxFolderPrefixLengthReached', { maxLengthPrefix })}`
              : ''
          }
          slotProps={{
            htmlInput: { maxLength: maxLengthPrefix },
          }}
        />

        <ModuleCheck module={ModuleTypes.WorkflowsModule}>
          <PermissionCheck permissions={[Permissions.ManageWorkflows]}>
            <WorkflowDefinitionPicker
              hideHeader
              value={definitionNumber}
              onChange={setDefinitionNumber}
              helperText={t('workflows.binding.optionalOnFolder')}
            />
          </PermissionCheck>
        </ModuleCheck>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'left', textAlign: 'center', gap: 1 }}>
        <LoadingButton loading={isLoading} variant="outlined" onClick={handleClose} id="mz5nnxqa">
          {`${t('shared.buttons.cancel')}`}
        </LoadingButton>

        <LoadingButton
          disabled={folderName.length === 0}
          loading={isLoading}
          variant="contained"
          onClick={handleSave}
          id="yd0sown5"
        >
          {`${t('shared.buttons.save')}`}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DLXDriveCreateFolderDialog;
